// Builds a Pokémon's evolution line from the Gradies API only. From the
// searched name it keeps stepping outward until nothing evolves further:
//   - down the line (evolves from) with
//       GET /cards?name=X&includeEvolvesFrom=true
//   - up the line (evolves into) with both
//       GET /cards?name=X&includeEvolvesTo=true     (the card's own evolves_to)
//       GET /cards?evolvesFrom=X                     (cards that evolve from X)
//     The reverse lookup matters: evolves_to data has gaps (no Charmeleon card
//     lists Charizard), while evolves_from is reliable.
//
// Card names are fuzzy ("Charizard ex", "M Charizard-EX", "Dark Charizard"),
// and /cards?name= is a contains-match, so each step:
//   - keeps only cards whose name contains the looked-up name as a whole word
//     (so "Mew" doesn't pull in Mewtwo), and skips tag-team cards
//     ("Charizard & Braixen-GX" would drag in Delphox);
//   - ignores evolutions that are just variants of the same Pokémon
//     ("Charizard VMAX" evolves from "Charizard V");
//   - collapses variant names onto the plain one ("Dark Charmeleon" and
//     "Charizard ex" are covered by "Charmeleon" and "Charizard", since listing
//     search is a contains-match too).

const API = process.env.REACT_APP_API_BASE_URL;
const MAX_STEPS = 5; // longest real line is 3; this just stops bad data looping
const CARD_LIMIT = 500;

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// True when `text` contains `word` as a whole word (case-insensitive).
export const hasWord = (text, word) =>
    !!text && !!word && new RegExp(`(^|[^\\p{L}\\p{N}])${escapeRegExp(word)}($|[^\\p{L}\\p{N}])`, 'iu').test(text);

const isTagTeam = (card) => (card?.name || '').includes('&');

const getCards = async (params, signal) => {
    const p = new URLSearchParams({ ...params, count: String(CARD_LIMIT) });
    const res = await fetch(`${API}/cards?${p}`, { signal });
    if (!res.ok) return [];
    const data = await res.json();
    return (data.cards || []).filter((c) => !isTagTeam(c));
};

// Cards named `name` (whole word), with the evolution field for `direction`.
const cardsNamed = async (name, direction, signal) => {
    const params = { name };
    if (direction === 'from') params.includeEvolvesFrom = 'true';
    if (direction === 'to') params.includeEvolvesTo = 'true';
    const cards = await getCards(params, signal);
    return cards.filter((c) => hasWord(c.name, name));
};

// Distinct names, minus any that are just a variant of a shorter name in the
// same list ("Dark Charmeleon" when "Charmeleon" is there).
const prune = (names) => {
    const unique = [...new Map(names.map((n) => [n.toLowerCase(), n])).values()];
    return unique.filter((n) => !unique.some((other) => other !== n && hasWord(n, other)));
};

// Names one step from `self` in `direction`, given `self`'s cards; excludes
// variants of `self` and anything already seen.
const nextNames = async (self, cards, direction, seen, signal) => {
    let raw;
    if (direction === 'from') {
        raw = cards.map((c) => c.evolves_from);
    } else {
        // Re-checks evolves_from so an API without the evolvesFrom filter
        // (which would return unrelated cards) can't add wrong names.
        const children = (await getCards({ evolvesFrom: self, includeEvolvesFrom: 'true' }, signal))
            .filter((c) => c.evolves_from === self);
        raw = [...cards.flatMap((c) => c.evolves_to || []), ...children.map((c) => c.name)];
    }
    return prune(raw.filter((n) => n && !hasWord(n, self) && !seen.has(n.toLowerCase())));
};

const walk = async (term, direction, signal) => {
    const seen = new Set([term.toLowerCase()]);
    const line = [];
    const startCards = await cardsNamed(term, direction, signal);
    let frontier = await nextNames(term, startCards, direction, seen, signal);
    for (let step = 0; step < MAX_STEPS && frontier.length > 0; step += 1) {
        frontier.forEach((n) => { seen.add(n.toLowerCase()); line.push(n); });
        // eslint-disable-next-line no-await-in-loop
        const next = await Promise.all(frontier.map(async (n) => {
            const cards = await cardsNamed(n, direction, signal);
            return nextNames(n, cards, direction, seen, signal);
        }));
        frontier = prune(next.flat());
    }
    return line;
};

// Resolves to { from: [...], to: [...] }, each closest relative first, e.g.
// "Charmander" -> { from: [], to: ['Charmeleon', 'Charizard'] }.
export const getEvolutionLine = async (term, signal) => {
    const name = (term || '').trim();
    if (name.length < 3) return { from: [], to: [] };
    const [from, to] = await Promise.all([
        walk(name, 'from', signal),
        walk(name, 'to', signal),
    ]);
    return { from, to };
};
